// Address codes from what a person (or their phone's autofill) actually types.
// Stephanie 2026-10-13: saving a quote's ship-to failed with a bare 400 because
// iPhone autofill filled Country with "United States" — the API stores the
// 2-letter code. These turn full names into codes; anything unrecognised is
// returned trimmed + upper-cased so the server's own length rule still decides.

const COUNTRIES: Record<string, string> = {
  "united states": "US", "united states of america": "US", usa: "US", "u.s.": "US",
  "u.s.a.": "US", us: "US", america: "US",
  canada: "CA", mexico: "MX", "united kingdom": "GB", uk: "GB", "great britain": "GB",
  england: "GB", ireland: "IE", australia: "AU", "new zealand": "NZ", germany: "DE",
  france: "FR", spain: "ES", italy: "IT", netherlands: "NL", "puerto rico": "PR",
};

const REGIONS: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", "district of columbia": "DC",
  "washington dc": "DC", "washington d.c.": "DC", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA", kansas: "KS",
  kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD", massachusetts: "MA",
  michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO", montana: "MT",
  nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC", "north dakota": "ND",
  ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI",
  "south carolina": "SC", "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  vermont: "VT", virginia: "VA", washington: "WA", "west virginia": "WV",
  wisconsin: "WI", wyoming: "WY", "puerto rico": "PR", guam: "GU",
  "virgin islands": "VI", "u.s. virgin islands": "VI",
  // Canadian provinces / territories
  alberta: "AB", "british columbia": "BC", manitoba: "MB", "new brunswick": "NB",
  "newfoundland and labrador": "NL", "nova scotia": "NS", ontario: "ON",
  "prince edward island": "PE", quebec: "QC", "québec": "QC", saskatchewan: "SK",
  "northwest territories": "NT", nunavut: "NU", yukon: "YT",
};

const key = (s: string): string => s.trim().replace(/\s+/g, " ").toLowerCase();

/** "United States" / "USA" / "us" → "US". Unknown → trimmed, upper-cased. */
export function normalizeCountryCode(input: string): string {
  return COUNTRIES[key(input)] ?? input.trim().toUpperCase();
}

/** "Illinois" / "il" → "IL", "Ontario" → "ON". Unknown → trimmed, upper-cased. */
export function normalizeStateCode(input: string): string {
  return REGIONS[key(input)] ?? input.trim().toUpperCase();
}

/** Dropdown choices for address forms — one entry per place, sorted by name.
 *  Choosing beats typing: a picked code can't be refused (Stephanie 2026-10-13). */
export const COUNTRY_OPTIONS: ReadonlyArray<{ code: string; name: string }> = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "MX", name: "Mexico" },
  { code: "PR", name: "Puerto Rico" },
  { code: "GB", name: "United Kingdom" },
  { code: "IE", name: "Ireland" },
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" },
];

const firstName = (code: string, skip: ReadonlySet<string>): string =>
  Object.entries(REGIONS).find(([n, c]) => c === code && !skip.has(n))?.[0] ?? code;
const titleCase = (s: string): string => s.replace(/(^|[\s.])([a-zé])/g, (_m, a: string, b: string) => a + b.toUpperCase());
const ALIASES: ReadonlySet<string> = new Set(["washington dc", "washington d.c.", "québec", "u.s. virgin islands"]);
const CA_CODES = ["AB", "BC", "MB", "NB", "NL", "NS", "ON", "PE", "QC", "SK", "NT", "NU", "YT"];

/** US states (+ DC and territories) and Canadian provinces for the State box. */
export const REGION_OPTIONS: Readonly<Record<"US" | "CA", ReadonlyArray<{ code: string; name: string }>>> = {
  US: [...new Set(Object.values(REGIONS))]
    .filter((c) => !CA_CODES.includes(c))
    .map((code) => ({ code, name: titleCase(firstName(code, ALIASES)).replace("Of Columbia", "of Columbia") }))
    .sort((a, b) => a.name.localeCompare(b.name)),
  CA: CA_CODES.map((code) => ({ code, name: titleCase(firstName(code, ALIASES)).replace("And Labrador", "and Labrador") }))
    .sort((a, b) => a.name.localeCompare(b.name)),
};
